"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection, TextSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import {
  FOCUS_NO_SCROLL,
  findConvertibleAncestor,
  findNodeAroundSelection,
  findNodePosition,
  getSelectedBlockNodes,
  isNodeInSchema,
  isNodeTypeSelected,
  isValidPosition,
  runWithPreservedWindowScroll,
  selectionWithinConvertibleTypes,
} from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { CodeBlockIcon } from "@/tiptap/icons/code-block-icon"

export const CODE_BLOCK_SHORTCUT_KEY = "mod+alt+c"
export const DEFAULT_CODE_BLOCK_LANGUAGE = "plaintext"

// Callouts can contain multiple paragraphs/lists. Converting each child with
// setCodeBlock would create one code block per line — collapse to a single
// code block with newlines instead (inverse of codeBlock → callout).
function replaceCalloutWithCodeBlock(editor) {
  const callout = findNodeAroundSelection(editor.state, "callout")
  if (!callout) return false

  const { state } = editor
  const { schema } = state
  const text = callout.node.textBetween(0, callout.node.content.size, "\n")
  const codeBlock = schema.nodes.codeBlock.create(
    { language: DEFAULT_CODE_BLOCK_LANGUAGE },
    text ? schema.text(text) : undefined,
  )

  editor.view.dispatch(
    state.tr.replaceWith(callout.pos, callout.pos + callout.node.nodeSize, codeBlock),
  )

  return true
}

/**
 * Checks if code block can be toggled in the current editor state
 */
export function canToggle(editor, turnInto = true) {
  if (!editor || !editor.isEditable) return false
  if (
    !isNodeInSchema("codeBlock", editor) ||
    isNodeTypeSelected(editor, ["image"])
  )
    return false

  if (!turnInto) {
    return editor.can().toggleNode("codeBlock", "paragraph");
  }

  // Ensure selection is in nodes we're allowed to convert
  if (
    !selectionWithinConvertibleTypes(editor, [
      "paragraph",
      "heading",
      "bulletList",
      "orderedList",
      "taskList",
      "blockquote",
      "callout",
      "codeBlock",
    ])
  )
    return false

  if (editor.isActive("callout") && findNodeAroundSelection(editor.state, "callout")) {
    return true
  }

  // Either we can toggle code block directly on the selection,
  // or we can clear formatting/nodes to arrive at a code block.
  return (editor.can().toggleNode("codeBlock", "paragraph") || editor.can().clearNodes());
}

/**
 * Toggles code block in the editor
 */
export function toggleCodeBlock(editor) {
  if (!editor || !editor.isEditable) return false
  if (!canToggle(editor)) return false

  return runWithPreservedWindowScroll(() => {
    try {
      if (editor.isActive("callout")) {
        return replaceCalloutWithCodeBlock(editor)
      }

      const view = editor.view
      let state = view.state
      let tr = state.tr

      const blocks = getSelectedBlockNodes(editor)

      // In case a selection contains multiple blocks, we only allow
      // toggling to nide if there's exactly one block selected
      // we also dont block the canToggle since it will fall back to the bottom logic
      const isPossibleToTurnInto =
        selectionWithinConvertibleTypes(editor, [
          "paragraph",
          "heading",
          "bulletList",
          "orderedList",
          "taskList",
          "blockquote",
          "callout",
          "codeBlock",
        ]) && blocks.length === 1

      // No selection, find the the cursor position
      if (
        (state.selection.empty || state.selection instanceof TextSelection) &&
        isPossibleToTurnInto
      ) {
        const pos = findNodePosition({
          editor,
          node: findConvertibleAncestor(state.selection),
        })?.pos
        if (!isValidPosition(pos)) return false

        tr = tr.setSelection(NodeSelection.create(state.doc, pos))
        view.dispatch(tr)
        state = view.state
      }

      const selection = state.selection

      let chain = editor.chain().focus(undefined, FOCUS_NO_SCROLL)

      // Handle NodeSelection
      if (selection instanceof NodeSelection) {
        const firstChild = selection.node.firstChild?.firstChild
        const lastChild = selection.node.lastChild?.lastChild

        const from = firstChild
          ? selection.from + firstChild.nodeSize
          : selection.from + 1

        const to = lastChild
          ? selection.to - lastChild.nodeSize
          : selection.to - 1

        const resolvedFrom = state.doc.resolve(from)
        const resolvedTo = state.doc.resolve(to)

        chain = chain
          .setTextSelection(TextSelection.between(resolvedFrom, resolvedTo))
          .clearNodes()
      }

      const toggle = editor.isActive("codeBlock")
        ? chain.setNode("paragraph")
        : chain.setCodeBlock({ language: DEFAULT_CODE_BLOCK_LANGUAGE })

      toggle.run()

      editor.chain().focus(undefined, FOCUS_NO_SCROLL).selectTextblockEnd().run()

      return true
    } catch {
      return false
    }
  })
}

/**
 * Determines if the code block button should be shown
 */
export function shouldShowButton(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  if (!editor.isEditable) return false

  if (!isNodeInSchema("codeBlock", editor)) return false

  if (!editor.isActive("code")) {
    return canToggle(editor);
  }

  return true
}

/**
 * Custom hook that provides code block functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage - no params needed
 * function MySimpleCodeBlockButton() {
 *   const { isVisible, isActive, handleToggle } = useCodeBlock()
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <button
 *       onClick={handleToggle}
 *       aria-pressed={isActive}
 *     >
 *       Code Block
 *     </button>
 *   )
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedCodeBlockButton() {
 *   const { isVisible, isActive, handleToggle, label } = useCodeBlock({
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: (isActive) => console.log('Code block toggled:', isActive)
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Toggle Code Block
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useCodeBlock(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onToggled,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const canToggleState = canToggle(editor)
  const isActive = editor?.isActive("codeBlock") || false

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    if (!editor) return false

    const success = toggleCodeBlock(editor)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, onToggled])

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle: canToggleState,
    label: "Code Block",
    shortcutKeys: CODE_BLOCK_SHORTCUT_KEY,
    Icon: CodeBlockIcon,
  }
}
